/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { getStations } from './services';
import { StationsResponse } from './interfaces';

interface StationProps {
    readonly onStationsChange: (value: number | number[] | null) => void;
    readonly stationsId?: readonly number[] | number | null;
    readonly disabled?: boolean;
    readonly single?: boolean;
}

export default function FilterAutocompleteStations({
    onStationsChange,
    stationsId,
    disabled = false,
    single = false,
}: StationProps) {
    const { translate } = useLang();
    const [options, setOptions] = useState<readonly StationsResponse[]>([]);
    const [value, setValue] = useState<StationsResponse[] | StationsResponse | null>(single ? null : []);
    const loading = useHookstate(false);

    const handleSingleSelection = (newValue: StationsResponse | null) => {
        setValue(newValue);
        onStationsChange(newValue ? Number(newValue.id) : null);
    };

    const handleMultipleSelection = (newValue: StationsResponse[]) => {
        setValue(newValue);
        onStationsChange(newValue.map(item => Number(item.id)));
    };

    useEffect(() => {
        getStations().then(stations => {
            setOptions(stations)
        })
    }, [])

    useEffect(() => {
        if (options.length > 0) {
            let selectedIds: number[] = [];
            if (single) {
                if (typeof stationsId === 'number') {
                    selectedIds = [stationsId];
                } else if (Array.isArray(stationsId)) {
                    selectedIds = [...stationsId];
                }
            } else {
                if (Array.isArray(stationsId)) {
                    selectedIds = [...stationsId];
                }
            }
            
            if (!selectedIds.length) {
                setValue(single ? null : [])
            } else {
                const selectedOptions = options.filter(option => 
                    selectedIds.includes(option.id)
                );
                
                if (single) {
                    setValue(selectedOptions[0] || null);
                } else {
                    setValue(selectedOptions);
                }
            }
        }
    }, [stationsId, options, single])

    return (
        <FormControl fullWidth size='small' >
            <Autocomplete
                filterSelectedOptions
                disableCloseOnSelect={!single}
                limitTags={2}
                sx={{ height: 1 }}
                ListboxProps={{ style: { maxHeight: '15rem' } }}
                id='filter-stations'
                multiple={!single}
                options={options}
                value={value}
                noOptionsText={translate('No options')}
                clearText={translate('Clear')}
                size='small'
                loading={loading.get()}
                disabled={disabled}
                isOptionEqualToValue={(option, val) => {
                    return option.id === (val as StationsResponse)?.id;
                }}
                getOptionLabel={(option) => option.name ?? ''}
                onChange={(event, newValue) => {
                    if (single) {
                        return handleSingleSelection(newValue as StationsResponse | null);
                    }
                    return handleMultipleSelection(newValue as StationsResponse[]);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={!single ? translate('Stations') : translate('Station')}
                    />
                )}
            />
        </FormControl>
    )
}